/* Coastal House — interactive layer on top of Dawn.
   Loaded from several sections, so everything is guarded to run once. */
(() => {
  if (window.__chTheme) return;
  window.__chTheme = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- helpers ---------- */

  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const formatMoney = (cents, format = '${{amount}}') => {
    const value = Number(cents) / 100;
    const withDelimiters = (number, decimals, thousands = ',', decimal = '.') => {
      const [whole, fraction] = number.toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return withDelimiters(value, 0);
        case 'amount_with_comma_separator':
          return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(value, 0, '.', ',');
        default:
          return withDelimiters(value, 2);
      }
    });
  };

  const addToCart = async (items) => {
    const drawer = document.querySelector('cart-drawer');
    const body = { items };
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      body.sections = drawer.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
    }
    const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || 'Could not add to bag');

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ch-theme', cartData: data });
    }
    if (drawer && data.sections) {
      drawer.classList.remove('is-empty');
      drawer.renderContents({ ...data, id: items[0]?.id });
    } else {
      window.location.href = window.routes?.cart_url || '/cart';
    }
    return data;
  };

  const setBusy = (button, busy) => {
    button.classList.toggle('is-loading', busy);
    button.toggleAttribute('aria-busy', busy);
    button.disabled = busy;
  };

  /* ---------- scroll reveal ---------- */

  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-in');
              revealObserver.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
        )
      : null;

  const initReveals = (root = document) => {
    root.querySelectorAll('.ch-reveal:not(.is-in)').forEach((el) => {
      if (revealObserver && !reduceMotion.matches) revealObserver.observe(el);
      else el.classList.add('is-in');
    });
  };

  /* ---------- parallax ---------- */

  const parallaxLayers = new Set();
  let parallaxTicking = false;
  const updateParallax = () => {
    parallaxTicking = false;
    const viewportCenter = window.innerHeight / 2;
    parallaxLayers.forEach((layer) => {
      const factor = parseFloat(layer.dataset.chParallaxLayer) || 0;
      const host = layer.closest('[data-ch-parallax]') || layer.parentElement;
      const rect = host.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;
      const offset = (rect.top + rect.height / 2 - viewportCenter) * factor;
      layer.style.setProperty('--ch-parallax', `${offset.toFixed(1)}px`);
    });
  };
  const initParallax = (root = document) => {
    if (reduceMotion.matches) return;
    root.querySelectorAll('[data-ch-parallax-layer]').forEach((layer) => parallaxLayers.add(layer));
    updateParallax();
  };
  window.addEventListener(
    'scroll',
    () => {
      if (parallaxTicking || !parallaxLayers.size) return;
      parallaxTicking = true;
      requestAnimationFrame(updateParallax);
    },
    { passive: true }
  );

  /* ---------- copy code ---------- */

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-ch-copy]');
    if (!button) return;
    const label = button.querySelector('[data-ch-copy-text]');
    try {
      await navigator.clipboard.writeText(button.dataset.chCopy);
      if (label) label.textContent = 'Copied';
      button.classList.add('is-copied');
    } catch (error) {
      if (label) label.textContent = button.dataset.chCopy;
    }
    setTimeout(() => {
      if (label) label.textContent = 'Copy';
      button.classList.remove('is-copied');
    }, 2200);
  });

  /* ---------- cart drawer upsell ---------- */

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-ch-upsell]');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('button[type="submit"]');
    setBusy(button, true);
    try {
      await addToCart([{ id: Number(new FormData(form).get('id')), quantity: 1 }]);
    } catch (error) {
      button.textContent = 'Try again';
    } finally {
      if (button.isConnected) setBusy(button, false);
    }
  });

  /* ---------- product card quick add ---------- */

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-ch-quick-add]');
    if (!button || button.disabled) return;
    event.preventDefault();
    const label = button.textContent;
    setBusy(button, true);
    try {
      await addToCart([{ id: Number(button.dataset.chQuickAdd), quantity: 1 }]);
      button.classList.add('is-added');
      setTimeout(() => button.classList.remove('is-added'), 1600);
    } catch (error) {
      button.textContent = 'Try again';
      setTimeout(() => (button.textContent = label), 1600);
    } finally {
      setBusy(button, false);
    }
  });

  /* ---------- ship-by estimate ---------- */

  const initShipBy = (root = document) => {
    root.querySelectorAll('[data-ch-ship-by]').forEach((el) => {
      const target = el.querySelector('[data-ch-ship-by-date]');
      if (!target) return;
      const maxDays = parseInt(el.dataset.max, 10) || 3;
      const cutoff = parseInt(el.dataset.cutoff, 10) || 14;
      const date = new Date();
      if (date.getHours() >= cutoff) date.setDate(date.getDate() + 1);
      let added = 0;
      while (added < maxDays) {
        date.setDate(date.getDate() + 1);
        const day = date.getDay();
        if (day !== 0 && day !== 6) added += 1;
      }
      const label = date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
      target.textContent = `, out the door by ${label}`;
    });
  };

  /* ---------- clothing rack ---------- */

  class ChRack extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const dataEl = this.querySelector('[data-ch-rack-data]');
      try {
        this.data = JSON.parse(dataEl?.textContent || '[]');
      } catch (error) {
        this.data = [];
      }
      this.list = this.querySelector('.ch-rack__items');
      this.items = [...this.querySelectorAll('.ch-rack__item')];
      this.buttons = this.items.map((item) => item.querySelector('.ch-garment'));
      this.swipeMode = window.matchMedia('(max-width: 749px)');
      this.stage = this.querySelector('.ch-rack__stage');
      this.caption = this.querySelector('.ch-rack__caption');
      this.active = undefined;
      this.locked = null;
      this.selectedVariant = null;

      this.panel = {
        root: this.querySelector('.ch-rack__panel'),
        img: this.querySelector('.ch-rack__panel-img'),
        badge: this.querySelector('.ch-rack__panel-badge'),
        count: this.querySelector('.ch-rack__panel-count'),
        link: this.querySelector('.ch-rack__panel-link'),
        price: this.querySelector('.ch-rack__panel-price'),
        sizes: this.querySelector('.ch-rack__sizes'),
        sizesLegend: this.querySelector('.ch-rack__sizes-legend'),
        sizesList: this.querySelector('.ch-rack__sizes-list'),
        form: this.querySelector('.ch-rack__form'),
        add: this.querySelector('.ch-rack__add'),
        addLabel: this.querySelector('.ch-rack__add-label'),
        error: this.querySelector('.ch-rack__error'),
        details: this.querySelector('.ch-rack__details'),
      };

      this.items.forEach((item, index) => {
        item.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse' && !this.swipeMode.matches) {
            this.stopSweep();
            this.preview(index);
          }
        });
      });
      this.buttons.forEach((button, index) => {
        button.addEventListener('focus', () => this.preview(index));
        button.addEventListener('click', () => {
          this.stopSweep();
          this.locked = index;
          this.preview(index, true);
          if (this.swipeMode.matches) this.center(index);
        });
      });
      this.list.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse' && !this.swipeMode.matches) this.preview(this.locked);
      });
      this.list.addEventListener('keydown', (event) => this.onKeydown(event));
      this.list.addEventListener('scroll', () => this.onScroll(), { passive: true });
      this.panel.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.panel.sizesList.addEventListener('change', (event) => {
        if (event.target.name !== this.sizeName) return;
        this.selectedVariant = Number(event.target.value);
        this.panel.error.hidden = true;
      });

      this.sizeName = `${this.id}-size`;
      this.renderPanel(0);
      if (this.swipeMode.matches) {
        requestAnimationFrame(() => this.preview(0));
      } else {
        this.observeIntro();
      }
    }

    observeIntro() {
      if (!('IntersectionObserver' in window)) return;
      const preload = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          preload.disconnect();
          this.data.forEach((product) => {
            if (product.image) new Image().src = product.image;
          });
        },
        { rootMargin: '600px 0px' }
      );
      preload.observe(this);
      if (reduceMotion.matches) return;
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          this.sweep();
        },
        { threshold: 0.45 }
      );
      observer.observe(this.list);
    }

    sweep() {
      let index = 0;
      const step = () => {
        if (index >= this.items.length) {
          this.sweepTimer = setTimeout(() => this.preview(this.locked), 260);
          return;
        }
        this.preview(index);
        index += 1;
        this.sweepTimer = setTimeout(step, 150);
      };
      this.sweepTimer = setTimeout(step, 250);
    }

    stopSweep() {
      clearTimeout(this.sweepTimer);
    }

    preview(index, force = false) {
      if (index === this.active && !force) return;
      this.active = index;
      const hasActive = index !== null && index !== undefined;
      this.classList.toggle('has-active', hasActive);
      this.items.forEach((item, position) => {
        let state = '';
        if (hasActive) state = position < index ? 'before' : position > index ? 'after' : 'active';
        item.dataset.state = state;
        item.style.zIndex = hasActive ? (position === index ? 100 : 50 - Math.abs(position - index)) : '';
        this.buttons[position].setAttribute('aria-pressed', position === this.locked ? 'true' : 'false');
      });
      this.updateCaption(hasActive ? index : null);
      if (!hasActive) return;
      const sway = this.items[index].querySelector('.ch-garment__sway');
      sway.classList.remove('is-swinging');
      void sway.offsetWidth;
      sway.classList.add('is-swinging');
      this.renderPanel(index);
    }

    updateCaption(index) {
      if (!this.caption) return;
      const product = index === null ? null : this.data[index];
      const label = this.caption.firstElementChild;
      if (!label) return;
      label.textContent = product ? product.title : '';
      if (!product) return;
      const item = this.items[index].getBoundingClientRect();
      const stage = this.caption.getBoundingClientRect();
      const half = Math.min(label.offsetWidth, stage.width) / 2;
      const x = Math.min(Math.max(item.left + item.width / 2 - stage.left, half), stage.width - half);
      this.caption.style.setProperty('--x', `${x}px`);
    }

    renderPanel(index) {
      const product = this.data[index];
      if (!product || this.panelIndex === index) return;
      this.panelIndex = index;
      const { panel } = this;
      const total = this.data.length;

      panel.root.classList.remove('is-swapping');
      void panel.root.offsetWidth;
      panel.root.classList.add('is-swapping');

      if (product.image) {
        panel.img.src = product.image;
        panel.img.alt = product.alt || product.title;
      }
      panel.badge.hidden = !product.badge;
      panel.badge.textContent = product.badge || '';
      panel.count.textContent = `${String(index + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;
      panel.link.textContent = product.title;
      panel.link.href = product.url;
      panel.details.href = product.url;
      panel.price.innerHTML = product.compare
        ? `<s>${escapeHtml(product.compare)}</s> <span class="ch-rack__sale">${escapeHtml(product.price)}</span>`
        : escapeHtml(product.price);
      panel.error.hidden = true;

      const variants = product.variants || [];
      const single = variants.length <= 1;
      this.selectedVariant = single ? variants[0]?.id ?? null : null;
      panel.sizes.hidden = single;
      if (!single) {
        panel.sizesLegend.textContent = product.optionLabel || 'Option';
        panel.sizesList.innerHTML = variants
          .map(
            (variant) => `
              <label class="ch-size${variant.available ? '' : ' is-sold-out'}">
                <input type="radio" name="${escapeHtml(this.sizeName)}" value="${variant.id}" ${variant.available ? '' : 'disabled'}>
                <span>${escapeHtml(variant.label)}</span>
              </label>`
          )
          .join('');
      }

      const soldOut = !product.available;
      panel.add.disabled = soldOut;
      panel.addLabel.textContent = soldOut ? 'Sold out' : 'Add to bag';
    }

    center(index) {
      const item = this.items[index];
      if (!item) return;
      const left = item.offsetLeft - (this.list.clientWidth - item.clientWidth) / 2;
      this.list.scrollTo({ left, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }

    onScroll() {
      if (!this.swipeMode.matches || this.scrollTicking) return;
      this.scrollTicking = true;
      requestAnimationFrame(() => {
        this.scrollTicking = false;
        const middle = this.list.scrollLeft + this.list.clientWidth / 2;
        let closest = 0;
        let distance = Infinity;
        this.items.forEach((item, index) => {
          const d = Math.abs(item.offsetLeft + item.clientWidth / 2 - middle);
          if (d < distance) {
            distance = d;
            closest = index;
          }
        });
        this.locked = closest;
        this.preview(closest);
      });
    }

    onKeydown(event) {
      const current = this.buttons.indexOf(document.activeElement);
      if (current === -1) return;
      let next = null;
      if (event.key === 'ArrowRight') next = Math.min(current + 1, this.buttons.length - 1);
      if (event.key === 'ArrowLeft') next = Math.max(current - 1, 0);
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = this.buttons.length - 1;
      if (next === null) return;
      event.preventDefault();
      this.buttons[next].focus();
      if (this.swipeMode.matches) this.center(next);
    }

    async onSubmit(event) {
      event.preventDefault();
      const { panel } = this;
      if (!this.selectedVariant) {
        panel.error.textContent = `Pick a ${(panel.sizesLegend.textContent || 'size').toLowerCase()} first.`;
        panel.error.hidden = false;
        panel.sizes.classList.remove('is-nudged');
        void panel.sizes.offsetWidth;
        panel.sizes.classList.add('is-nudged');
        panel.sizesList.querySelector('input:not([disabled])')?.focus();
        return;
      }
      setBusy(panel.add, true);
      try {
        await addToCart([{ id: this.selectedVariant, quantity: 1 }]);
        panel.addLabel.textContent = 'Added';
        setTimeout(() => (panel.addLabel.textContent = 'Add to bag'), 2000);
      } catch (error) {
        panel.error.textContent = error.message;
        panel.error.hidden = false;
      } finally {
        setBusy(panel.add, false);
      }
    }
  }
  customElements.define('ch-rack', ChRack);

  /* ---------- bundle ---------- */

  class ChBundle extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.form = this.querySelector('form');
      this.totalEl = this.querySelector('[data-total]');
      this.shipEl = this.querySelector('[data-ship]');
      this.errorEl = this.querySelector('.ch-bundle__error');
      this.button = this.querySelector('.ch-bundle__add');
      this.threshold = Number(this.dataset.threshold) || 0;
      this.format = this.dataset.moneyFormat;
      this.form.addEventListener('change', () => this.update());
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.update();

      if (this.hasAttribute('data-follow-main') && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          const select = this.form.querySelector('select[name="id-0"]');
          const option = data?.variant && select?.querySelector(`option[value="${data.variant.id}"]`);
          if (!option || option.disabled) return;
          select.value = String(data.variant.id);
          this.update();
        });
      }
    }

    disconnectedCallback() {
      this.unsubscribe?.();
    }

    fields() {
      return [...this.form.querySelectorAll('select[name^="id-"], input[name^="id-"]')];
    }

    priceOf(field) {
      const source = field.tagName === 'SELECT' ? field.selectedOptions[0] : field;
      return Number(source?.dataset.priceCents) || 0;
    }

    update() {
      let total = 0;
      this.fields().forEach((field) => {
        const price = this.priceOf(field);
        total += price;
        const priceEl = field.closest('.ch-bundle__item')?.querySelector('[data-price]');
        if (priceEl) priceEl.textContent = formatMoney(price, this.format);
      });
      this.totalEl.textContent = formatMoney(total, this.format);
      if (this.shipEl) this.shipEl.hidden = !(this.threshold > 0 && total >= this.threshold);
    }

    async onSubmit(event) {
      event.preventDefault();
      this.errorEl.hidden = true;
      const items = this.fields().map((field) => ({ id: Number(field.value), quantity: 1 }));
      setBusy(this.button, true);
      try {
        await addToCart(items);
      } catch (error) {
        this.errorEl.textContent = error.message;
        this.errorEl.hidden = false;
      } finally {
        setBusy(this.button, false);
      }
    }
  }
  customElements.define('ch-bundle', ChBundle);

  /* ---------- countdown ---------- */

  class ChCountdown extends HTMLElement {
    connectedCallback() {
      this.end = Date.parse(this.dataset.end);
      if (Number.isNaN(this.end) || this.end <= Date.now()) return;
      this.units = Object.fromEntries([...this.querySelectorAll('[data-unit]')].map((el) => [el.dataset.unit, el]));
      this.hidden = false;
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    tick() {
      const remaining = Math.max(0, this.end - Date.now());
      if (remaining === 0) {
        clearInterval(this.timer);
        this.hidden = true;
        return;
      }
      const seconds = Math.floor(remaining / 1000);
      const values = {
        d: Math.floor(seconds / 86400),
        h: Math.floor((seconds % 86400) / 3600),
        m: Math.floor((seconds % 3600) / 60),
        s: seconds % 60,
      };
      Object.entries(values).forEach(([key, value]) => {
        if (this.units[key]) this.units[key].textContent = String(value).padStart(2, '0');
      });
    }
  }
  customElements.define('ch-countdown', ChCountdown);

  /* ---------- sticky add to cart ---------- */

  class ChStickyAtc extends HTMLElement {
    connectedCallback() {
      this.main = document.querySelector('[id^="MainProduct-"]');
      this.submit = this.main?.querySelector('[id^="ProductSubmitButton-"]');
      if (!this.main || !this.submit) return;
      this.hidden = false;
      this.button = this.querySelector('[data-ch-sticky-add]');
      this.label = this.querySelector('[data-ch-sticky-label]');
      this.variantEl = this.querySelector('[data-ch-sticky-variant]');
      this.priceEl = this.querySelector('[data-ch-sticky-price]');

      this.button.addEventListener('click', () => {
        if (this.submit.disabled) {
          this.main.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth' });
          return;
        }
        this.submit.click();
      });

      const observer = new IntersectionObserver(([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', passed);
        document.body.classList.toggle('ch-has-sticky-atc', passed);
      });
      observer.observe(this.submit);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => this.update(data?.variant));
      }
    }

    disconnectedCallback() {
      this.unsubscribe?.();
    }

    update(variant) {
      if (!variant) {
        this.button.disabled = true;
        this.label.textContent = window.variantStrings?.unavailable || 'Unavailable';
        return;
      }
      if (this.variantEl) this.variantEl.textContent = variant.title === 'Default Title' ? '' : variant.title;
      const mainPrice = this.main.querySelector('.price-item--last, .price-item--regular');
      if (this.priceEl && mainPrice) this.priceEl.textContent = mainPrice.textContent.trim();
      this.button.disabled = !variant.available;
      this.label.textContent = variant.available
        ? window.variantStrings?.addToCart || 'Add to cart'
        : window.variantStrings?.soldOut || 'Sold out';
    }
  }
  customElements.define('ch-sticky-atc', ChStickyAtc);

  /* ---------- collection sort ---------- */

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-ch-sort] select');
    if (!select) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', select.value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  });

  /* ---------- boot ---------- */

  const playHeroVideos = (root = document) => {
    if (reduceMotion.matches) return;
    root.querySelectorAll('.ch-hero__media video').forEach((video) => {
      video.muted = true;
      if (video.paused) video.play().catch(() => {});
    });
  };

  const boot = (root = document) => {
    playHeroVideos(root);
    initReveals(root);
    initParallax(root);
    initShipBy(root);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();

  document.addEventListener('shopify:section:load', (event) => boot(event.target));

  // Content injected later (e.g. product recommendations) still gets its reveal animation.
  if ('MutationObserver' in window) {
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1 && (node.matches('.ch-reveal') || node.querySelector('.ch-reveal'))) {
            initReveals(node.parentElement || document);
          }
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
